import axios, {
   AxiosInstance,
   AxiosResponse,
   InternalAxiosRequestConfig,
} from "axios"
import { getMockStripePricesResponse } from "@/mocks/stripePrices"
import { clerk, getOrgId } from "./clerk"

import type {
   DBVariable,
   Project,
   ProjectSettings,
   UpdateProjectSettingsPayload,
} from "../types"
import type {
   GetStripePricesParams,
   StripePrice,
   CheckoutSessionResponse,
} from "../types/stripe.d"
import type { EditableVariable } from "../types/variable"
import { logInfo } from "@utils/logging"
import { useProjectStore } from "@stores/projectStore"
import { addBreadcrumb } from "./sentry"
import {
   ProjectOnboardingResponse,
   TourCompletionResponse,
   ResetAllToursResponse,
} from "@/types/onboarding"
import { getMockProjectOnboarding } from "@/mocks/onboarding"

const adminBackendClient: AxiosInstance = axios.create({
   baseURL: <string>import.meta.env.VITE_API_SERVER_URL,
   timeout: 5000,
})

adminBackendClient.interceptors.request.use(
   async (config: InternalAxiosRequestConfig) => {
      await clerk.load()
      const token = await clerk.session?.getToken()
      if (!token) {
         logInfo("No Clerk token found, redirecting to sign-in")
         window.location.href = "/sign-in"
      }
      addBreadcrumb({
         category: "API",
         message: `Calling Admin Backend API: ${config.url}`,
         level: "info",
      })
      if (config.headers) {
         config.headers.Authorization = `Bearer ${token}`
      }
      return config
   },
   (error) => {
      console.log("Error in request interceptor")
      const s = error.response.status
      if (s === 401 || s === 403) {
         addBreadcrumb({
            category: "Auth",
            message: `Unauthorized: ${error.response.config.url}`,
            level: "error",
         })
         console.log("Unauthorized")
      }
   },
)

export default adminBackendClient

const getVariablesByProjectId = async (
   projectId: number,
): Promise<AxiosResponse<DBVariable[]>> => {
   const response = await adminBackendClient.get(
      `/v2/projects/${projectId}/variables`,
   )
   return response
}

const getVariable = async (id: number): Promise<AxiosResponse<DBVariable>> => {
   const { projectId } = useProjectStore()
   return await adminBackendClient.get(
      `/v2/projects/${projectId}/variables/${id}`,
   )
}

const updateVariable = async (
   id: number,
   payload: EditableVariable,
): Promise<AxiosResponse<DBVariable>> => {
   const { projectId } = useProjectStore()
   return await adminBackendClient.patch(
      `/v2/projects/${projectId}/variables/${id}`,
      payload,
   )
}

const deleteVariable = async (id: number): Promise<AxiosResponse<boolean>> => {
   const { projectId } = useProjectStore()
   return await adminBackendClient.delete(
      `/v2/projects/${projectId}/variables/${id}`,
   )
}

const createVariable = async (
   payload: EditableVariable,
): Promise<AxiosResponse<DBVariable>> => {
   const { projectId } = useProjectStore()
   return await adminBackendClient.post(
      `/v2/projects/${projectId}/variables`,
      payload,
   )
}

const getProjectSettings = async (
   projectId: number,
): Promise<AxiosResponse<ProjectSettings>> => {
   return await adminBackendClient.get(`/v2/projects/${projectId}/settings`)
}

const updateActiveProjectSettings = async (
   payload: UpdateProjectSettingsPayload,
): Promise<AxiosResponse<ProjectSettings>> => {
   const { projectId } = useProjectStore()
   return await adminBackendClient.patch(
      `/v2/projects/${projectId}/settings`,
      payload,
   )
}

const getProjectsByActiveOrg = async (): Promise<AxiosResponse<Project[]>> => {
   return await adminBackendClient.get(`/v2/projects`)
}

const createProject = async (
   humanReadableName: string,
   orgId: string,
): Promise<AxiosResponse<Project>> => {
   return await adminBackendClient.post(`/v2/projects`, {
      humanReadableName: humanReadableName,
      organizationId: orgId,
   })
}

const deleteProject = async (
   projectId: number,
): Promise<AxiosResponse<boolean>> => {
   return await adminBackendClient.delete(`/v2/projects/${projectId}`)
}

const updateProject = async (
   projectId: number,
   orgId: string,
   humanReadableName: string,
): Promise<AxiosResponse<Project>> => {
   return await adminBackendClient.patch(`/v2/projects/${projectId}`, {
      humanReadableName: humanReadableName,
      organizationId: orgId,
   })
}

const updateCurrentProject = async (
   humanReadableName: string,
): Promise<AxiosResponse<Project>> => {
   const { projectId } = useProjectStore()
   const orgId = await getOrgId()
   if (!projectId) {
      throw new Error("No project selected")
   }
   if (!orgId) {
      throw new Error("No orgId found")
   }
   return await updateProject(projectId, orgId, humanReadableName)
}

const getStripePrices = async (
   params?: GetStripePricesParams,
): Promise<
   AxiosResponse<{
      data: StripePrice[]
      total_count: number
   }>
> => {
   // Mock data in development mode
   if (import.meta.env.VITE_MOCK_EMPLOYEE === "true") {
      return getMockStripePricesResponse(params)
   }

   // Real API call in production
   return await adminBackendClient.get(`/v2/admin/stripe/prices`, { params })
}

const createInvitation = async (
   emailAddress: string,
   stripePriceId: string,
): Promise<AxiosResponse<void>> => {
   return await adminBackendClient.post(`/v2/admin/clerk/invitation`, {
      email_address: emailAddress,
      stripe_price_id: stripePriceId,
   })
}

// Create Stripe checkout session
const createStripeCheckoutSession = async (): Promise<
   AxiosResponse<{ url: string }>
> => {
   return await adminBackendClient.post(`/v2/stripe/checkout-session`)
}

// Create Stripe checkout session for tier
const createStripeCheckoutSessionForTier = async (
   tier: "starter_tier" | "professional_tier",
): Promise<AxiosResponse<CheckoutSessionResponse>> => {
   return await adminBackendClient.post(
      `/v2/stripe/checkout-session/tier/${tier}`,
   )
}

const getProjectOnboarding = async (): Promise<
   AxiosResponse<ProjectOnboardingResponse>
> => {
   const projectId = useProjectStore().projectId
   if (!projectId) {
      throw new Error("No project selected")
   }
   if (
      import.meta.env.VITE_ENV === "dev" &&
      import.meta.env.VITE_MOCK_PROJECT_ONBOARDING === "true"
   ) {
      return getMockProjectOnboarding(projectId)
   }
   return await adminBackendClient.get(`/v2/projects/${projectId}/onboarding`)
}

// Create Stripe customer portal session
const createStripeCustomerPortalSession = async (): Promise<
   AxiosResponse<{ url: string }>
> => {
   return await adminBackendClient.post(`/v2/stripe/customer-portal`)
}

/**
 * Fetch onboarding status including tour completion status
 * @returns Promise with onboarding status response
 */
const getOnboardingStatus = async (): Promise<
   AxiosResponse<ProjectOnboardingResponse>
> => {
   const { projectId } = useProjectStore()
   return await adminBackendClient.get(`/v2/projects/${projectId}/onboarding`)
}

/**
 * Mark a specific tour as completed
 * @param tourId - ID of the tour to mark as completed
 * @returns Promise with tour completion response
 */
const markTourCompleted = async (
   tourId: string,
): Promise<AxiosResponse<TourCompletionResponse>> => {
   return await adminBackendClient.put(`/v2/tours/${tourId}`)
}

/**
 * Reset a specific tour's completion status
 * @param tourId - ID of the tour to reset
 * @returns Promise with tour completion response
 */
const resetTourCompletion = async (
   tourId: string,
): Promise<AxiosResponse<TourCompletionResponse>> => {
   return await adminBackendClient.delete(`/v2/tours/${tourId}`)
}

/**
 * Reset all tours' completion status
 * @returns Promise with reset all tours response
 */
const resetAllTours = async (): Promise<
   AxiosResponse<ResetAllToursResponse>
> => {
   return await adminBackendClient.delete("/v2/tours")
}

export {
   adminBackendClient,
   getProjectOnboarding,
   getVariable,
   getVariablesByProjectId,
   updateVariable,
   deleteVariable,
   createVariable,
   getProjectSettings,
   updateActiveProjectSettings,
   getProjectsByActiveOrg,
   createProject,
   deleteProject,
   updateProject,
   updateCurrentProject,
   getStripePrices,
   createInvitation,
   createStripeCheckoutSession,
   createStripeCheckoutSessionForTier,
   createStripeCustomerPortalSession,
   getOnboardingStatus,
   markTourCompleted,
   resetTourCompletion,
   resetAllTours,
}
